// client/src/pages/admin/ManageProducts.js

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

export default function ManageProducts() {
  const token = localStorage.getItem('authToken');
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  // Form fields
  const [productId, setProductId] = useState(null);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');
  const [stock, setStock] = useState('');
  const [isAccessory, setIsAccessory] = useState(false);
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');

  // File inputs for new uploads
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);

  // Existing media for the product being edited
  const [existingImages, setExistingImages] = useState([]);
  const [existingVideos, setExistingVideos] = useState([]);

  // Which ones to remove
  const [removedImages, setRemovedImages] = useState([]);
  const [removedVideos, setRemovedVideos] = useState([]);

  useEffect(() => {
    fetchCategories();
    fetchProducts();
    // eslint-disable-next-line
  }, []);

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/category`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/product`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(data);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const handleSubmit = async () => {
    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('category', category);
      formData.append('price', price);
      formData.append('description', description);
      formData.append('stock', stock);
      formData.append('isAccessory', isAccessory);
      formData.append('seoTitle', seoTitle);
      formData.append('seoDescription', seoDescription);

      images.forEach(img => formData.append('images', img));
      videos.forEach(vid => formData.append('videos', vid));

      // Pass arrays of removed images/videos
      removedImages.forEach(path => formData.append('removedImages', path));
      removedVideos.forEach(path => formData.append('removedVideos', path));

      if (!productId) {
        // CREATE
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/admin/product`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        alert('Product created successfully!');
      } else {
        // UPDATE
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/admin/product/${productId}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        alert('Product updated successfully!');
      }

      // reset form
      clearForm();
      fetchProducts();
    } catch (err) {
      console.error('Error creating/updating product:', err);
      alert('Failed to create/update product');
    }
  };

  const clearForm = () => {
    setProductId(null);
    setName('');
    setCategory('');
    setPrice('');
    setDescription('');
    setStock('');
    setIsAccessory(false);
    setSeoTitle('');
    setSeoDescription('');
    setImages([]);
    setVideos([]);
    setExistingImages([]);
    setExistingVideos([]);
    setRemovedImages([]);
    setRemovedVideos([]);
  };

  const handleEdit = (prod) => {
    setProductId(prod._id);
    setName(prod.name);
    setCategory(prod.category?._id || '');
    setPrice(prod.price);
    setDescription(prod.description);
    setStock(prod.stock);
    setIsAccessory(prod.isAccessory);
    setSeoTitle(prod.seoTitle || '');
    setSeoDescription(prod.seoDescription || '');

    // existing media
    setExistingImages(prod.images || []);
    setExistingVideos(prod.videos || []);

    setImages([]);
    setVideos([]);
    setRemovedImages([]);
    setRemovedVideos([]);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/admin/product/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Product deleted');
      fetchProducts();
    } catch (err) {
      console.error('Error deleting product:', err);
    }
  };

  // When admin clicks "Remove" on an existing image
  const removeExistingImage = (path) => {
    setRemovedImages([...removedImages, path]); 
    setExistingImages(existingImages.filter(i => i !== path));
  };

  // When admin clicks "Remove" on an existing video
  const removeExistingVideo = (path) => {
    setRemovedVideos([...removedVideos, path]);
    setExistingVideos(existingVideos.filter(v => v !== path));
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Manage Products</h1>

      {/* CREATE/EDIT FORM */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block mb-1 font-medium">Name</label>
          <input
            type="text"
            className="border p-2 w-full"
            value={name}
            onChange={e => setName(e.target.value)}
          />
        </div>
        <div>
          <label className="block mb-1 font-medium">Category</label>
          <select
            className="border p-2 w-full"
            value={category}
            onChange={e => setCategory(e.target.value)}
          >
            <option value="">Select Category</option>
            {categories.map(cat => (
              <option key={cat._id} value={cat._id}>{cat.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block mb-1 font-medium">Price</label>
          <input
            type="number"
            className="border p-2 w-full"
            value={price}
            onChange={e => setPrice(e.target.value)}
          />
        </div>
        <div>
          <label className="block mb-1 font-medium">Stock</label>
          <input
            type="number"
            className="border p-2 w-full"
            value={stock}
            onChange={e => setStock(e.target.value)}
          />
        </div>
        <div className="flex items-center space-x-2 mt-2">
          <input
            type="checkbox"
            checked={isAccessory}
            onChange={e => setIsAccessory(e.target.checked)}
          />
          <span className="font-medium">Is Accessory?</span>
        </div>
        <div>
          <label className="block mb-1 font-medium">SEO Title</label>
          <input
            type="text"
            className="border p-2 w-full"
            value={seoTitle}
            onChange={e => setSeoTitle(e.target.value)}
          />
        </div>
        <div>
          <label className="block mb-1 font-medium">SEO Description</label>
          <input
            type="text"
            className="border p-2 w-full"
            value={seoDescription}
            onChange={e => setSeoDescription(e.target.value)}
          />
        </div>
        <div className="col-span-2">
          <label className="block mb-1 font-medium">Description</label>
          <ReactQuill theme="snow" value={description} onChange={setDescription} />
        </div>

        {/* New images */}
        <div>
          <label className="block mb-1 font-medium">Add Images (multiple)</label>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={e => setImages(Array.from(e.target.files))}
          />
        </div>
        {/* New videos */}
        <div>
          <label className="block mb-1 font-medium">Add Videos (multiple)</label>
          <input
            type="file"
            multiple
            accept="video/*"
            onChange={e => setVideos(Array.from(e.target.files))}
          />
        </div>
      </div>

      {/* Existing images and videos (if editing) */}
      {productId && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2">Existing Images</h3>
          <div className="flex flex-wrap gap-2">
            {existingImages.map((imgPath, idx) => (
              <div key={idx} className="relative w-24 h-24 border">
                <img
                  src={`${process.env.REACT_APP_API_URL}/${imgPath}`}
                  alt="Product"
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => removeExistingImage(imgPath)}
                  className="absolute top-0 right-0 bg-red-500 text-white text-xs px-1 py-0.5 rounded"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          <h3 className="text-lg font-semibold mt-4 mb-2">Existing Videos</h3>
          <div className="flex flex-wrap gap-2">
            {existingVideos.map((vidPath, idx) => (
              <div key={idx} className="relative w-32 border p-1">
                <video
                  src={`${process.env.REACT_APP_API_URL}/${vidPath}`}
                  controls
                  className="w-full"
                />
                <button
                  type="button"
                  onClick={() => removeExistingVideo(vidPath)}
                  className="absolute top-0 right-0 bg-red-500 text-white text-xs px-1 py-0.5 rounded"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={handleSubmit}
        className="bg-primaryGreen text-white px-4 py-2 rounded hover:bg-green-500"
      >
        {productId ? 'Update Product' : 'Add Product'}
      </button>

      <hr className="my-6" />

      {/* LIST EXISTING PRODUCTS */}
      <h2 className="text-xl font-bold mb-4">Existing Products</h2>
      <div className="space-y-4">
        {products.map(prod => (
          <div key={prod._id} className="border p-3 rounded flex justify-between items-start">
            <div>
              <h3 className="text-lg font-semibold">{prod.name}</h3>
              <p>Price: ${prod.price} | Stock: {prod.stock}</p>
              <p className="text-sm text-gray-600">
                Category: {prod.category?.name}
              </p>
              <div
                className="mt-1 text-gray-700"
                dangerouslySetInnerHTML={{ __html: prod.description }}
              />
              <p className="text-sm mt-1">
                <strong>Images:</strong> {prod.images?.length || 0},
                <strong> Videos:</strong> {prod.videos?.length || 0}
              </p>
            </div>
            <div className="space-x-2">
              <button
                onClick={() => handleEdit(prod)}
                className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600"
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(prod._id)}
                className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
